import React from 'react';
import { Select } from 'antd';
import { useQuery } from '@apollo/client';
import {
  FIND_DRIVERS_BY_DSP_IDS,
  FindDriversByDspIdsResponse,
  FindDriversByDspIdsVariables
} from '../../pages/delivery/splitOrder/graphql/deliveryRoutes';
import { t } from '../../i18n';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../utils/languageUtils';

interface Driver {
  accountId: number;
  name?: string | null;
  phone?: string | null;
}

interface MultiDriverSelectFilterProps {
  label: string;
  value?: number[];
  onChange: (value: number[] | undefined) => void;
  dspIds?: number[];  // 选中的 DSP IDs（自动查询模式）
  drivers?: Driver[];  // 直接传入司机列表（手动模式）
  spanClassName?: string;
  className?: string;
  placeholder?: string;
}

export const MultiDriverSelectFilter: React.FC<MultiDriverSelectFilterProps> = ({
  label,
  value,
  onChange,
  dspIds,
  drivers: externalDrivers,
  spanClassName,
  className,
  placeholder
}) => {
  // 只有当使用 dspIds 模式且 dspIds 有值时才查询司机
  const { data, loading } = useQuery<FindDriversByDspIdsResponse, FindDriversByDspIdsVariables>(
    FIND_DRIVERS_BY_DSP_IDS,
    {
      variables: { dspIds: dspIds || [] },
      skip: !!externalDrivers || !dspIds || dspIds.length === 0  // 如果提供了外部司机列表，跳过查询
    }
  );

  // 使用外部传入的司机列表或查询到的司机列表
  const drivers = externalDrivers || data?.findDriversByDspIds || [];

  const options = React.useMemo(() => {
    return drivers.map(driver => ({
      label: `${driver.name} ${driver.phone ? `(${driver.phone})` : ''}`,
      value: driver.accountId
    }));
  }, [drivers]);

  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();

  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <div className={inputContainerClassName}>
        <Select
          showSearch
          allowClear
          mode="multiple"
          placeholder={placeholder !== undefined ? placeholder : t('components.input.all')}
          value={value}
          onChange={(val) => onChange(val && val.length > 0 ? val as number[] : undefined)}
          loading={loading}
          options={options}
          filterOption={(input, option) => {
            if (!option) return false;
            const searchText = input.toLowerCase();
            const driver = drivers.find(d => d.accountId === option.value);
            if (!driver) return false;
            return !!(
              driver.name?.toLowerCase().includes(searchText) ||
              driver.phone?.toLowerCase().includes(searchText)
            );
          }}
          className="w-full"
          size="middle"
          maxTagCount="responsive"
        />
      </div>
    </div>
  );
};

export default MultiDriverSelectFilter;

